/**
 * Matching for the FAQ search box.
 *
 * Two things make or break FAQ search for this audience:
 *
 * 1. **Accents are optional.** Brazilians type `nao consigo sacar` on a phone
 *    keyboard far more often than `não consigo sacar`. A search that respects
 *    diacritics silently returns nothing for the most common phrasing.
 * 2. **Users search their words, not ours.** Nobody types "rollover"; they type
 *    "dinheiro travado". Entries carry a hidden alias list so their vocabulary
 *    reaches our answer.
 *
 * The fold table is explicit rather than `normalize('NFD')` + strip, because
 * highlighting maps a hit position back onto the ORIGINAL string — every fold
 * has to be exactly one character wide, which an explicit table guarantees and
 * Unicode normalization does not.
 */

const FOLD: Record<string, string> = {
  á: 'a', à: 'a', ã: 'a', â: 'a', ä: 'a', å: 'a',
  é: 'e', è: 'e', ê: 'e', ë: 'e',
  í: 'i', ì: 'i', î: 'i', ï: 'i',
  ó: 'o', ò: 'o', õ: 'o', ô: 'o', ö: 'o',
  ú: 'u', ù: 'u', û: 'u', ü: 'u',
  ç: 'c', ñ: 'n', ý: 'y',
};

/** Lowercase + strip accents, one input character to one output character. */
export function fold(text: string): string {
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!.toLowerCase();
    out += FOLD[ch] ?? ch;
  }
  return out;
}

/**
 * Split a query into search terms. `R$` survives because amounts are a real
 * thing people search ("minimo r$30"); everything else non-alphanumeric splits.
 */
export function queryTerms(query: string): string[] {
  return fold(query)
    .split(/[^a-z0-9$]+/)
    .filter((term) => term.length > 0);
}

/**
 * Every term must appear somewhere in the entry, so typing more words narrows
 * the list instead of widening it. An empty query matches everything.
 */
export function matchesTerms(haystack: string, terms: string[]): boolean {
  if (terms.length === 0) return true;
  const folded = fold(haystack);
  return terms.every((term) => folded.includes(term));
}

export type HighlightSegment = { text: string; hit: boolean };

/**
 * Split `text` into alternating plain and matched segments so the UI can mark
 * the hit. Overlapping matches are merged; the concatenated segments always
 * reproduce the input exactly.
 */
export function highlight(text: string, terms: string[]): HighlightSegment[] {
  const plain: HighlightSegment[] = [{ text, hit: false }];
  if (terms.length === 0 || text.length === 0) return plain;

  const folded = fold(text);
  const spans: Array<[number, number]> = [];
  for (const term of terms) {
    let from = 0;
    for (;;) {
      const at = folded.indexOf(term, from);
      if (at < 0) break;
      spans.push([at, at + term.length]);
      from = at + term.length;
    }
  }
  if (spans.length === 0) return plain;

  spans.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [start, end] of spans) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }

  const out: HighlightSegment[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) out.push({ text: text.slice(cursor, start), hit: false });
    out.push({ text: text.slice(start, end), hit: true });
    cursor = end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), hit: false });
  return out;
}

/** `|` is the list separator inside a single i18n value — see faqCatalog. */
export function splitList(value: string): string[] {
  return value.split('|').map((part) => part.trim()).filter((part) => part.length > 0);
}
